import * as React from "react"
import ReactModal from "react-modal"
import NSLocalizedString from "../utils/NSLocalizedString"
import { ConnectionTest, ConnectionTestGroup, eltDescription } from "./domutils"
import "./submitModal.css"

enum SubmitButtonState {
    NotReady,
    Ready,
    Sending,
    Done
}

type SubmitModalProps = {
    tests: Array<ConnectionTest | ConnectionTestGroup>,
    modalIsOpen: boolean,
    closeModal: () => void
}

type SubmitModalState = {
    email: string,
    submitButtonState: SubmitButtonState,
    submitMessage: string | null
}

export class SubmitModal extends React.Component<SubmitModalProps, SubmitModalState> {

    constructor(props: SubmitModalProps) {
        super(props)

        this.state = {
            email: "",
            submitButtonState: SubmitButtonState.NotReady,
            submitMessage: null
        }
    }

    handleEmailChange: React.FormEventHandler<HTMLInputElement> = (e) => {
        let currentState = this.state.submitButtonState
        if (currentState === SubmitButtonState.Sending || currentState === SubmitButtonState.Done) {
            return
        }

        let newEmail = e.currentTarget.value
        let newState = (newEmail === "") ? SubmitButtonState.NotReady : SubmitButtonState.Ready
        this.setState({
            email: newEmail,
            submitButtonState: newState
        })
    }

    sendReportDisabled = () => {
        return this.state.submitButtonState !== SubmitButtonState.Ready
    }

    submitButtonTitle = () => {
        switch (this.state.submitButtonState) {
            case SubmitButtonState.Sending: return NSLocalizedString("Sending…", "submit modal button label");
            case SubmitButtonState.Done: return NSLocalizedString("Done", "submit modal button label");
            default: return NSLocalizedString("Submit", "submit modal button label");
        }
    }

    handleSubmitReportLoadEnd = (e: ProgressEvent) => {
        if (e.target instanceof XMLHttpRequest) {
            const req = e.target as XMLHttpRequest
            if (req.status >= 400) {
                this.setState({
                    submitButtonState: SubmitButtonState.Ready,
                    submitMessage: NSLocalizedString("Unable to submit report. Check your email address and try again.", "submit modal status message"),
                })
            } else {
                this.setState({
                    submitButtonState: SubmitButtonState.Done,
                    submitMessage: NSLocalizedString("Thank you for submitting this report. You may now close this page.", "submit modal status message"),
                })
            }
        }
    }

    sendReport = (e: React.MouseEvent) => {
        e.preventDefault()

        const formData = new FormData()
        formData.append("account", "undefined")
        formData.append("email", this.state.email)
        formData.append("message", this.buildMessage())

        const req = new XMLHttpRequest();
        req.open("POST", "https://feedback.omnifocus.com/send-feedback/OmniFocus-Web");
        req.addEventListener('loadend', this.handleSubmitReportLoadEnd)

        req.send(formData);
        this.setState({
            submitButtonState: SubmitButtonState.Sending,
            submitMessage: null
        })
    }

    // Report message generation

    testDescription = (ct: ConnectionTest | ConnectionTestGroup, indent: number): string => {
        let spaces = "    ".repeat(indent)
        let prefix = `${spaces}- `
        if (ct.hasOwnProperty('status')) {
            let test = ct as ConnectionTest
            let description = eltDescription(test.summary)
            if (test.details) {
                let detailSummary = test.details.map(detail => {
                    // Add two more spaces to bump out into the same bullet
                    return `${spaces}  ${eltDescription(detail)}`
                }).join("\n")
                return `${prefix}${description}\n${detailSummary}`
            } else {
                return `${prefix}${description}`
            }
        } else {
            let group = ct as ConnectionTestGroup
            let summaryText = prefix + eltDescription(group.summary)
            let detailTexts = group.subtests.map(st => { return this.testDescription(st, indent + 1) })
            return `${summaryText}\n${detailTexts.join("\n")}`
        }
    }

    buildMessage() {
        return this.props.tests.map(test => this.testDescription(test, 0)).join("\n")
    }

    // Modal helpers

    performCloseModal = () => {
        this.setState({ email: "", submitButtonState: SubmitButtonState.NotReady })
        this.props.closeModal()
    }

    afterOpenModal = () => {
        console.log("modal opened");
    }

    modalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)'
        }
    }


    render() {
        // Bind modal to the app element (http://reactcommunity.org/react-modal/accessibility/)
        ReactModal.setAppElement('#contentbox')
        const submitReportTitle = NSLocalizedString("Submit a report to the Omni Group Support team", "submit report title");

        return (
            <ReactModal
                isOpen={this.props.modalIsOpen}
                onAfterOpen={this.afterOpenModal}
                onRequestClose={this.performCloseModal}
                contentLabel={submitReportTitle}
                style={this.modalStyles}
            >
                <div id="modalContent">
                    <div id="modalHeaderChunk" className="chunk">
                        <h3>{ submitReportTitle }</h3>
                        <p>{ NSLocalizedString("Your report will include your email address and the following test summary.", "submit report dialog") }</p>
                    </div>
                    <hr/>
                    <form method="POST" action="">
                        <div id="modalSummaryChunk" className="chunk">
                            <p>{ NSLocalizedString("Test Summary", "submit report dialog") }</p>
                            <div id="reportMessage">{ this.buildMessage() }</div>
                        </div>
                        <div id="modalControlsChunk" className="chunk">
                            <p>{ NSLocalizedString("Your Email Address", "submit report dialog") }</p>
                            <div id="modalControlsFlexbox">
                                <input id="reportEmail" type="email" name="email" onChange={ this.handleEmailChange }/>
                                <button id="submitReport" disabled={this.sendReportDisabled()} onClick={this.sendReport}>{ this.submitButtonTitle() }</button>
                                <button id="closeModal" onClick={this.performCloseModal}>{ NSLocalizedString("Cancel", "submit report dialog") }</button>
                            </div>
                        </div>
                    </form>
                    <div id="modalFooterChunk" className="chunk">
                        <p>{this.state.submitMessage}</p>
                    </div>
                </div>
            </ReactModal>
        )

    }

}
